import { type ElementType, type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { AlertCircle, BellRing, Download, Loader2, Play } from 'lucide-react';
import { useMutationState, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { markNotificationMutationKey, useNotifications } from '@/hooks/data/notifications/useNotifications';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { useNotificationsChannel } from '@/hooks/data/notifications/useNotificationsChannel';
import { NotificationDropdownContent } from '@/components/ui/notification-dropdown-content';
import { NotificationType, isNotificationType } from '@/utilities/notification-types';
import { NotificationBellTrigger } from '@/components/ui/notification-bell-trigger';
import { useShareExportDownload } from '@/hooks/data/export/useShareExportDownload';
import { DropdownMenu, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useExportDownload } from '@/hooks/data/export/useExportDownload';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { type Notification } from '@/types/notifications';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useTreeContext } from '@/context/TreeContext';
import { ToastVariant } from '@/components/ui/toast';
import { DAMNodeType, NodeType } from '@/types/tree';
import { getAsset } from '@/services/asset.service';
import { error as errorLog } from '@/utilities/log';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { QueryKey } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

// FIXME: Temp work around for DM-1860
export const DISABLE_NOTIFICATION_NAVIGATION = true;

const NotificationItem = ({
  notification,
  onMarkAsRead,
  isUpdating = false,
  isDropdownOpen,
  onNotificationClick,
}: {
  notification: Notification;
  onMarkAsRead: (read: boolean) => Promise<unknown> | void;
  isUpdating?: boolean;
  isDropdownOpen: boolean;
  onNotificationClick?: (notification: Notification) => Promise<void>;
}) => {
  const { title, description, readAt, type, cta } = notification;
  const { ref, inView } = useInView({ threshold: 0.5 });
  const tCommon = useTranslations('common');
  const tNotifications = useTranslations('notifications');
  const isArchiveReady = isNotificationType(notification, NotificationType.ArchiveReady);
  const isCommentResolved = isNotificationType(notification, NotificationType.CommentResolved);
  const isCommentCreated = isNotificationType(notification, NotificationType.CommentCreated);
  const isShareExpired = isNotificationType(notification, NotificationType.ShareExpired);
  const isShareCreated = isNotificationType(notification, NotificationType.ShareCreated);
  const ctaTitle =
    cta?.cta_title ?? (isArchiveReady ? tCommon('download') : isShareCreated ? tNotifications('viewShare') : undefined);
  const clickableWithoutCta = !cta && onNotificationClick && (isShareExpired || isCommentResolved || isCommentCreated);

  const isRead = Boolean(readAt);
  const viewTimerRef = useRef<NodeJS.Timeout | null>(null);
  const wasInViewRef = useRef(false);

  const Icon: ElementType | undefined = useMemo(() => {
    switch (type) {
      case NotificationType.ArchiveReady:
        return Download;
      case NotificationType.UploadComplete:
        return BellRing;
      case NotificationType.Welcome:
        return Play;
      default:
        return undefined;
    }
  }, [type]);

  const handleSetRead = useCallback(
    (nextRead: boolean) => {
      if (isUpdating || isRead === nextRead) {
        return;
      }

      return onMarkAsRead(nextRead);
    },
    [isRead, isUpdating, onMarkAsRead],
  );

  useEffect(() => {
    const clearTimer = () => {
      if (viewTimerRef.current) {
        clearTimeout(viewTimerRef.current);
        viewTimerRef.current = null;
      }
    };

    // Don't mark notification as read if the dropdown closes or the notification goes out of view
    if (!isDropdownOpen || !inView) {
      clearTimer();
      wasInViewRef.current = false;

      return;
    }

    // If the notification is already read or currently updating, do nothing
    if (isRead || isUpdating) {
      clearTimer();
      wasInViewRef.current = true;

      return;
    }

    // If the notification was manually marked as read while in view,
    // do not start the timer again until the notification re-enters view
    if (!wasInViewRef.current) {
      wasInViewRef.current = true;
      viewTimerRef.current = setTimeout(() => {
        void handleSetRead(true);
        viewTimerRef.current = null;
      }, 5000);
    }
  }, [handleSetRead, inView, isRead, isUpdating, isDropdownOpen]);

  return (
    <div
      data-cy={`notification-item-${notification.id}`}
      ref={ref}
      className={cn('flex items-start gap-3 bg-ui-panel p-3 transition hover:bg-ui-medium', {
        'hover:cursor-pointer': Boolean(clickableWithoutCta),
      })}
      {...(clickableWithoutCta && {
        onClick: () => {
          void onNotificationClick(notification);
        },
      })}
    >
      <Button
        variant="ghost"
        size="icon"
        className="icon-7 rounded-full border border-transparent p-0! duration-150 hover:border-ui-bevel! disabled:opacity-100"
        aria-label={isRead ? tNotifications('markAsUnread') : tNotifications('markAsRead')}
        data-cy="notification-read-toggle"
        onClick={(e) => {
          e.stopPropagation();
          void handleSetRead(!isRead);
        }}
        disabled={isUpdating}
        tooltip={isRead ? tNotifications('markAsUnread') : tNotifications('markAsRead')}
      >
        <span
          className={cn('rounded-full bg-highlight-base icon-3', {
            'bg-ui-light': isRead,
          })}
          aria-hidden
        />
      </Button>
      <div className="flex w-full flex-col text-xs">
        <Tooltip delayDuration={750}>
          <TooltipTrigger asChild>
            <p className="line-clamp-1 font-semibold wrap-anywhere text-text-base">{title}</p>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-64 break-all">{title}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
        <Tooltip delayDuration={750}>
          <TooltipTrigger asChild>
            <p className="line-clamp-2 wrap-anywhere text-text-gray">{description}</p>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-64 break-all">{description}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </div>
      {ctaTitle && (
        <Button
          variant="secondary"
          className="flex gap-x-2 rounded-xl p-3 font-bold whitespace-nowrap text-text-gray!"
          data-cy="notification-cta"
          onClick={async (e) => {
            e.stopPropagation();

            if (notification && onNotificationClick) {
              await onNotificationClick(notification);
            }
          }}
          disabled={isUpdating}
        >
          {Icon && <Icon className="icon-4" strokeWidth={3} />}
          {ctaTitle}
        </Button>
      )}
    </div>
  );
};

export const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);

  const tCommon = useTranslations('common');
  const tErrors = useTranslations('errors');
  const tNotifications = useTranslations('notifications');

  const updatingNotificationIds = useMutationState({
    filters: { mutationKey: markNotificationMutationKey, status: 'pending' },
    select: (mutation) => (mutation.state.variables as { notificationId?: string } | undefined)?.notificationId,
  });
  const updatingIdSet = useMemo(
    () => new Set(updatingNotificationIds.filter((id): id is string => Boolean(id))),
    [updatingNotificationIds],
  );
  const setLocateAssetId = useBrowserStore((state) => state.setLocateAssetId);
  const getAssetFn = useAuthenticatedQueryFn(getAsset);
  const setIsAllSelected = useBrowserStore((state) => state.setIsAllSelected);
  const queryClient = useQueryClient();

  const { push, query } = useRouter();
  const { setSelectedAssets } = useGallerySelection();
  const { setSelectedNodes, setCurrentSelectedType } = useTreeContext();
  const { browseRedirection } = useCurrentPage();
  const { isAuthenticated } = useAuth0();
  const { downloadExport } = useExportDownload();
  // OTP guests lack permission on the authenticated export route, so a share-scoped
  // ArchiveReady notification downloads through the share endpoint instead. shareId
  // comes from the share page the guest is on (matches the notification's shareId).
  const currentShareId = typeof query.shareId === 'string' ? query.shareId : '';
  const { downloadExport: downloadShareExport } = useShareExportDownload(currentShareId);

  const {
    notifications,
    unreadCount,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    markAllAsRead,
    isMarkingAll,
    markNotificationRead,
  } = useNotifications(
    {
      pagination: {
        offset: 0,
        limit: 12,
      },
      sort: {
        order: SortOrderOptions.DESC,
        value: SortOrderValues.CREATED_AT,
      },
    },
    { enabled: isAuthenticated },
  );

  useEffect(() => {
    if (isOpen) {
      void refetch();
    }
  }, [isOpen, refetch]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }

      const target = event.currentTarget;
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceFromBottom < 64) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const handleMarkAsRead = useCallback(
    async (notificationId: string, read: boolean) => {
      if (updatingIdSet.has(notificationId)) {
        return;
      }

      await markNotificationRead({ notificationId, read });
    },
    [markNotificationRead, updatingIdSet],
  );

  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      const isArchiveReady = isNotificationType(notification, NotificationType.ArchiveReady);
      const isShareExpired = isNotificationType(notification, NotificationType.ShareExpired);
      const isShareCreated = isNotificationType(notification, NotificationType.ShareCreated);
      // CommentCreated carries the same payload shape as CommentResolved --
      // commentId/replyId/shareId alongside a cta_link that is either a
      // share-scoped path (guest) or a bare node id (DAM user) -- so both open
      // the comments inspector through the same two branches below.
      const isCommentNotification =
        isNotificationType(notification, NotificationType.CommentResolved) ||
        isNotificationType(notification, NotificationType.CommentCreated);

      if (isArchiveReady) {
        if (!notification.readAt) {
          await handleMarkAsRead(notification.id, true);
        }

        const { exportId } = notification;

        if (!exportId) {
          toast({
            variant: ToastVariant.DESTRUCTIVE,
            title: tCommon('error'),
            description: tErrors('toastDefaultDescription'),
          });
          return;
        }

        if (notification.shareId) {
          await downloadShareExport(exportId, notification.shareId);
        } else {
          await downloadExport(exportId);
        }
        return;
      }

      if (DISABLE_NOTIFICATION_NAVIGATION && isShareExpired) {
        if (!notification.readAt) {
          await handleMarkAsRead(notification.id, true);
        }

        return;
      }

      const { cta, nodeType, link, commentId, replyId } = notification;

      if (isShareCreated) {
        if (!notification.readAt) {
          await handleMarkAsRead(notification.id, true);
        }

        if (link) {
          const shareUrl = new URL(link, window.location.origin);
          await push(`${shareUrl.pathname}${shareUrl.search}${shareUrl.hash}`);
        }

        return;
      }

      const normalizedLink = cta
        ? cta?.cta_link?.startsWith('/')
          ? cta.cta_link.slice(1)
          : cta?.cta_link
        : link?.startsWith('/')
          ? link.slice(1)
          : link;

      const ctaTarget =
        nodeType === DAMNodeType.Album
          ? `/browse/${normalizedLink}`
          : nodeType === DAMNodeType.Asset
            ? `/asset/${normalizedLink}`
            : null;

      if (!notification.readAt) {
        await handleMarkAsRead(notification.id, true);
      }

      if (isCommentNotification && notification.shareId && cta?.cta_link?.startsWith('/shares/')) {
        const shareUrl = new URL(cta.cta_link, window.location.origin);
        shareUrl.searchParams.set('inspectorTab', 'inspector-comments');
        if (commentId) {
          shareUrl.searchParams.set('commentId', commentId);
        }
        if (replyId) {
          shareUrl.searchParams.set('replyId', replyId);
        }

        await push(`${shareUrl.pathname}${shareUrl.search}`);
        return;
      }

      if (isCommentNotification && normalizedLink) {
        try {
          const assetInfo = await getAssetFn({ id: normalizedLink });
          const assetNodeId = assetInfo.nodeId ?? normalizedLink;

          setSelectedAssets([assetInfo]);
          // TEMPORARY DOUBLE-WRITE of the older TreeContext.selectedNodes store — a follow-up PR
          // untangles the two. See TreeContext.selectedNodes.
          setSelectedNodes([{ id: assetNodeId, name: assetInfo.name, type: DAMNodeType.Asset }]);
          setCurrentSelectedType(NodeType.Assets);
          setLocateAssetId(undefined);
          setIsAllSelected(false);

          const targetAssetId = assetInfo.id ?? normalizedLink;
          const searchParams = new URLSearchParams({
            inspectorTab: 'inspector-comments',
            ...(commentId && { commentId }),
            ...(replyId && { replyId }),
          });

          await push(`/asset/${targetAssetId}?${searchParams.toString()}`);
          return;
        } catch (error: any) {
          errorLog('Failed to open asset comment notification:', error);

          toast({
            variant: ToastVariant.DESTRUCTIVE,
            title: tCommon('error'),
            description:
              error.status === 404
                ? tErrors('toastEntityDeleted', { resource: 'asset' })
                : (error.message ?? tErrors('toastDefaultDescription')),
          });

          if (error.status === 404) {
            void queryClient.invalidateQueries({ queryKey: [QueryKey.NotificationList] });
          }
        }
      }

      if (nodeType === DAMNodeType.Asset && link) {
        try {
          // We need to fetch because findNodePath may not have the path in the tree yet
          const assetInfo = await getAssetFn({ id: link });
          setLocateAssetId(link);
          const assetNodeId = assetInfo.nodeId ?? link;
          setSelectedAssets([assetInfo]);
          // TEMPORARY DOUBLE-WRITE of the older TreeContext.selectedNodes store — a follow-up PR
          // untangles the two. See TreeContext.selectedNodes.
          setSelectedNodes([{ id: assetNodeId, name: assetInfo.name, type: DAMNodeType.Asset }]);
          setCurrentSelectedType(NodeType.Assets);
          if (assetInfo.ancestors && assetInfo.ancestors.length > 0) {
            const pathArray = assetInfo.ancestors.map((ancestor) => ancestor.slug);
            // Drop both query and filters so locating an asset fully exits search mode.
            // search is now derived from the URL — a lingering `filters` param would re-activate it
            // once locateAssetId clears.
            await browseRedirection(pathArray, undefined, undefined, ['query', 'filters']);
          }
          setIsAllSelected(false);
          return;
        } catch (error) {
          errorLog('Failed to locate asset from notification click:', error);
        }
      }
      if (nodeType === DAMNodeType.Album && ctaTarget) {
        setSelectedAssets([]);
        setSelectedNodes([]);
        setLocateAssetId(undefined);
        setIsAllSelected(false);
        setCurrentSelectedType(NodeType.Albums);
        await push(ctaTarget);
      }
    },
    [
      browseRedirection,
      downloadExport,
      downloadShareExport,
      getAssetFn,
      handleMarkAsRead,
      push,
      setCurrentSelectedType,
      setIsAllSelected,
      setLocateAssetId,
      setSelectedAssets,
      setSelectedNodes,
      tErrors,
    ],
  );

  useNotificationsChannel({ markNotificationRead, handleNotificationClick });

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <NotificationBellTrigger
          unreadCount={unreadCount}
          label={tNotifications('notificationsLabel')}
          triggerDataCy="notifications-trigger"
          badgeDataCy="notifications-unread-badge"
        />
      </DropdownMenuTrigger>
      <NotificationDropdownContent
        dataCy="notifications-dropdown"
        className="min-h-96"
        markAll={{
          label: tNotifications('markAllRead'),
          dataCy: 'notifications-mark-all',
          disabled: isLoading || unreadCount === 0 || isMarkingAll,
          onClick: () => {
            void markAllAsRead();
          },
        }}
      >
        {isLoading ? (
          <div className="flex grow items-center justify-center gap-2 bg-ui-panel p-4 text-sm text-text-gray">
            <Loader2 className="icon-4 animate-spin" />
            {tNotifications('loading')}
          </div>
        ) : isError ? (
          <div className="flex grow flex-col items-center justify-center gap-3 bg-ui-panel p-4 text-sm text-text-gray">
            <div className="flex items-center gap-2 text-status-failure">
              <AlertCircle className="icon-4" />
              {tNotifications('loadError')}
            </div>
            <Button variant="secondary" size="sm" onClick={() => refetch()} className="rounded-xl">
              {tNotifications('tryAgain')}
            </Button>
          </div>
        ) : !isFetchingNextPage && !hasNextPage && notifications.length === 0 ? (
          <div className="flex grow items-center justify-center bg-ui-panel p-4 text-sm text-text-gray">
            {tNotifications('emptyState')}
          </div>
        ) : (
          <ScrollArea
            data-cy="notifications-list"
            className="relative flex max-h-96 w-full grow flex-col overflow-x-hidden overflow-y-auto bg-ui-container"
            onScroll={handleScroll}
          >
            <div className="relative flex min-h-full w-full grow flex-col space-y-px overflow-x-hidden overflow-y-auto bg-ui-void-gap last:pb-px">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={(read) => handleMarkAsRead(notification.id, read)}
                  isUpdating={updatingIdSet.has(notification.id)}
                  isDropdownOpen={isOpen}
                  onNotificationClick={handleNotificationClick}
                />
              ))}
              {isFetchingNextPage && (
                <div className="flex items-center justify-center gap-2 bg-ui-panel p-4 text-xs text-text-gray">
                  <Loader2 className="icon-6 animate-spin" />
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </NotificationDropdownContent>
    </DropdownMenu>
  );
};
